import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Eye, EyeOff, AlertCircle, Clock, MessageCircle, Smartphone,
  Sparkles, FileSignature, CalendarClock, ScanText, MessageSquare, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { USE_MOCK } from '../services/apiClient';
import QrCode from '../components/auth/QrCode';
import RecoveryCodes from '../components/auth/RecoveryCodes';
import LegalFooter from '../components/common/LegalFooter';
import inditechLogo from '../assets/inditech.png';

/** How long the branded hand-off screen stays up after a successful sign-in. */
const HANDOFF_MS = 3000;

/**
 * What the Hub actually does, on the one screen everybody sees.
 *
 * This replaced three statistics — "7,505 documents indexed", "8 departments" — that were
 * hard-coded and wrong: there were 37 and 24. A number on a sign-in page is either fetched
 * and true, or it is a claim the product cannot keep, and fetching it would mean telling an
 * unauthenticated visitor how much Inditech holds. Capabilities have neither problem.
 */
const FEATURES = [
  { icon: Sparkles, title: 'AI search', sub: 'Find a clause by meaning, not by filename' },
  { icon: FileSignature, title: 'e-Signature', sub: 'Sign and countersign without leaving the Hub' },
  { icon: CalendarClock, title: 'Renewals', sub: 'Licences and visas chased before they lapse' },
  { icon: ScanText, title: 'OCR', sub: 'Reads inside scans, English and Arabic' },
  { icon: MessageSquare, title: 'Comments', sub: 'The discussion stays beside the document' },
  { icon: ShieldCheck, title: 'Retention & legal hold', sub: 'Keep what you must, dispose of the rest' },
];

/**
 * v2 login: dark hero on the left, centred sign-in panel on the right.
 * MFA is a second step revealed inside the same panel.
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfa, status, signedOutReason, clearSignedOutReason } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [handoff, setHandoff] = useState(false);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const codeRef = useRef(null);

  const from = location.state?.from?.pathname || '/dashboard';

  // Offering a sign-in method that the organisation has turned off would be a button that
  // cannot work, so the answer is fetched before the form is drawn.
  useEffect(() => {
    let cancelled = false;
    authService.publicConfig().then((cfg) => {
      if (!cancelled) setSsoEnabled(Boolean(cfg?.allowMicrosoftSso));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Signing in normally hands off to the requested page. Two things hold that back: the
  // recovery codes, which are readable only on this screen, and the hand-off animation.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    // The codes are on screen, or about to be: enrolling issues them, and they are
    // readable only here. Hold until they have been acknowledged.
    if (recoveryCodes) return undefined;
    if (challenge?.enrolmentRequired && !codesAcknowledged) return undefined;

    setHandoff(true);
    const timer = setTimeout(() => navigate(from, { replace: true }), HANDOFF_MS);
    return () => clearTimeout(timer);
  }, [status, navigate, from, recoveryCodes, challenge?.enrolmentRequired, codesAcknowledged]);

  // Countdown to the next permitted resend.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const beginChallenge = useCallback((result) => {
    setChallenge(result);
    setCode('');
    setResendIn(result.resendAfterSeconds || 60);
    // Development convenience: the API returns the code when nothing can deliver it.
    if (result.devCode) setNotice(`Development mode — your code is ${result.devCode}`);
  }, []);

  const resend = async () => {
    if (!challenge || resendIn > 0 || busy) return;
    setBusy(true);
    setError(null);
    clearSignedOutReason();
    try {
      const result = await authService.resendMfa(challenge.challengeId);
      beginChallenge({ ...challenge, ...result });
      setNotice(result.devCode ? `Development mode — your code is ${result.devCode}` : 'A new code is on its way.');
      codeRef.current?.focus();
    } catch (err) {
      setError(err.message || 'That code could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (challenge) {
      // A recovery code is letters and digits; an authenticator code is six digits.
      const digits = /[a-z]/i.test(code) ? code.trim() : code.replace(/\D/g, '');
      if (!/[a-z]/i.test(digits) && digits.length !== 6) {
        setError(challenge.method === 'totp' ? 'Enter the 6-digit code from your authenticator app.' : 'Enter the 6-digit code we sent you.');
        return;
      }
      setBusy(true);
      try {
        const result = await verifyMfa({ challengeId: challenge.challengeId, code: digits, remember });
        // Enrolling produces recovery codes, which are readable only at this moment.
        // Otherwise the effect above takes over and runs the hand-off.
        if (result?.recoveryCodes?.length) setRecoveryCodes(result.recoveryCodes);
      } catch (err) {
        setError(err.message || 'That code is not valid.');
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!email.trim() || !password) {
      setError('Enter your email and password to continue.');
      return;
    }
    setBusy(true);
    try {
      const result = await login({ email: email.trim(), password, remember });
      if (result.mfaRequired) beginChallenge(result);
      // Without a second factor the session is already live, and the effect above hands
      // over. Leaving `busy` set keeps the form from being submitted twice in between.
    } catch (err) {
      setError(err.message || 'Incorrect email or password.');
    } finally {
      setBusy(false);
    }
  };

  const demoAccounts = USE_MOCK ? authService.demoAccounts() : [];

  // Signed in: hold on the brand for a moment rather than snapping straight to a
  // half-loaded dashboard.
  if (handoff) {
    return (
      <div className="handoff">
        <div className="handoff__inner">
          {/* The name above the mark. This screen is the moment between signing in and the
              workspace appearing — what somebody needs from it is confirmation they are
              landing in the right place, and a logo alone does not say that. */}
          <div className="wordmark">
            <b>Inditech</b> <span>Document Hub</span>
          </div>
          <img src={inditechLogo} alt="" className="handoff__logo" />
          <div className="handoff__bar" role="progressbar" aria-label="Preparing your workspace">
            <i />
          </div>
          <div className="handoff__text">Preparing your workspace…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <aside className="login-hero">
        <div className="row" style={{ gap: 10 }}>
          <span className="brand-mark" style={{ width: 36, height: 36, borderRadius: 10, fontSize: 16 }}>
            iD
          </span>
          <span style={{ fontWeight: 600 }}>Inditech Document Hub</span>
        </div>

        <div>
          <h1 style={{ margin: '0 0 16px' }}>One secure place for every Inditech document.</h1>
          <p style={{ margin: '0 0 30px' }}>
            Departments, roles and permissions you configure. Every version kept, every action logged.
          </p>
          <div className="login-features">
            {FEATURES.map(({ icon: Icon, title, sub }) => (
              <div key={title} className="login-fact">
                <Icon size={17} className="login-fact__icon" />
                <div style={{ minWidth: 0 }}>
                  <div className="login-fact__title">{title}</div>
                  <div className="login-fact__sub">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 12.5, color: '#64748b' }}>Secure. Organized. Accessible. Intelligent.</div>
      </aside>

      <main className="login-panel">
        {recoveryCodes ? (
          <div className="panel login-form">
            <RecoveryCodes
              codes={recoveryCodes}
              onDone={() => {
                setCodesAcknowledged(true);
                setRecoveryCodes(null);
              }}
              doneLabel="Continue to the Hub"
            />
          </div>
        ) : (
        <form className="panel login-form" onSubmit={submit}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: 24, letterSpacing: '-0.01em' }}>
              {challenge?.enrolmentRequired ? 'Set up your authenticator' : 'Sign in'}
            </h2>
            <div className="muted" style={{ fontSize: 13.5 }}>
              {challenge?.enrolmentRequired
                ? 'Your role requires two-step verification. This takes about a minute and is needed once.'
                : 'Use your Inditech account.'}
            </div>
          </div>

          {/* Why they are looking at a sign-in screen they did not ask for. Shown above the
              error so a stale timeout notice never sits on top of a fresh wrong-password. */}
          {signedOutReason === 'idle' && !error && (
            <div className="banner">
              <Clock size={16} />
              <div>You were signed out after a period of inactivity. Sign in to carry on.</div>
            </div>
          )}

          {error && (
            <div className="banner banner--danger">
              <AlertCircle size={16} />
              <div>{error}</div>
            </div>
          )}

          <div>
            <label className="lbl" htmlFor="email">
              Email or username
            </label>
            <input
              id="email"
              className="in"
              type="email"
              autoComplete="username"
              placeholder="name@inditech.me"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy || Boolean(challenge)}
            />
          </div>

          <div>
            <label className="lbl" htmlFor="password">
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                className="in"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy || Boolean(challenge)}
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                className="input-group__action"
                style={{ top: 4 }}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {challenge?.enrolment && (
            <div className="stack" style={{ gap: 12 }}>
              <div className="row" style={{ gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <QrCode value={challenge.enrolment.otpauthUri} alt="Scan with your authenticator app" />
                <div className="stack" style={{ gap: 8, minWidth: 200, flex: 1 }}>
                  <div style={{ fontSize: 13 }}>
                    <strong>1.</strong> Install Google Authenticator, Microsoft Authenticator or 1Password.
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <strong>2.</strong> Scan this code, or enter the key by hand:
                  </div>
                  <code
                    style={{
                      fontSize: 12.5,
                      letterSpacing: '.08em',
                      wordBreak: 'break-all',
                      background: 'var(--surface-3)',
                      padding: '6px 8px',
                      borderRadius: 6,
                    }}
                  >
                    {challenge.enrolment.secret}
                  </code>
                  <div style={{ fontSize: 13 }}>
                    <strong>3.</strong> Enter the 6-digit code it shows.
                  </div>
                </div>
              </div>
            </div>
          )}

          {challenge && (
            <div className="screen" style={{ background: 'var(--ac-t)', border: '1px solid var(--ac-l)', borderRadius: 10, padding: 14 }}>
              <label className="lbl" htmlFor="code" style={{ color: 'var(--ac-d)' }}>
                <span className="row" style={{ gap: 6 }}>
                  {challenge.method === 'totp' ? <Smartphone size={14} /> : <MessageCircle size={14} />}
                  {challenge.method === 'totp' ? 'Authenticator code' : 'Verification code'}
                </span>
              </label>
              <input
                id="code"
                ref={codeRef}
                className="in"
                inputMode={challenge.method === 'totp' ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                maxLength={11}
                placeholder="000 000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                style={{ letterSpacing: '.2em', fontWeight: 600, fontSize: 18, height: 44 }}
              />
              <div style={{ fontSize: 12, color: 'var(--ac-d)', marginTop: 6 }}>
                {challenge.method === 'totp'
                  ? challenge.enrolmentRequired
                    ? 'The code changes every 30 seconds.'
                    : 'From your authenticator app. You can also use a recovery code.'
                  : `Sent on WhatsApp to ${challenge.maskedDestination}. It expires in 5 minutes.`}
              </div>
              {challenge.method !== 'totp' && (
                <div className="row" style={{ gap: 6, marginTop: 8, fontSize: 12 }}>
                  <button type="button" className="link" onClick={resend} disabled={resendIn > 0 || busy}>
                    Send a new code
                  </button>
                  {resendIn > 0 && <span className="muted">in {resendIn}s</span>}
                </div>
              )}
            </div>
          )}

          {notice && (
            <div className="callout" style={{ fontSize: 12.5 }}>
              {notice}
            </div>
          )}

          <label className="checkbox" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Keep me signed in on this device
          </label>

          <button type="submit" className="btn btn-p btn-lg" disabled={busy}>
            {busy ? 'Please wait…' : challenge?.enrolmentRequired ? 'Confirm and finish setup' : challenge ? 'Verify and continue' : 'Continue'}
          </button>

          <div className="row" style={{ justifyContent: 'space-between', fontSize: 12.5 }}>
            <Link to="/forgot-password" style={{ fontWeight: 600 }}>
              Forgot password?
            </Link>
            <span className="muted">Two-step verification · TLS</span>
          </div>

          {!challenge && ssoEnabled && (
            <>
              <div className="row" style={{ gap: 12, fontSize: 12.5, color: 'var(--faint)' }}>
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                or
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
              <button
                type="button"
                className="btn btn-s"
                onClick={() => {
                  if (USE_MOCK) setError('Microsoft sign-in is federated through Amazon Cognito in the deployed environment.');
                  else window.location.href = authService.ssoUrl();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
                Sign in with Microsoft
              </button>
            </>
          )}

          {demoAccounts.length > 0 && !challenge && (
            <div className="callout" style={{ fontSize: 12.5 }}>
              <strong>Demo accounts</strong> — any password works.
              <div className="stack" style={{ gap: 4, marginTop: 6 }}>
                {demoAccounts.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="row"
                    style={{ gap: 6, background: 'none', border: 0, padding: 0, textAlign: 'left', color: 'var(--ac-dd)', cursor: 'pointer' }}
                    onClick={() => {
                      setEmail(a.email);
                      setPassword('demo');
                    }}
                  >
                    <code style={{ fontFamily: 'var(--font)', fontWeight: 600 }}>{a.email}</code>
                    <span style={{ opacity: 0.7 }}>— {a.roleName}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <img src={inditechLogo} alt="Inditech" className="login-logo" />
        </form>
        )}

        <LegalFooter />
      </main>
    </div>
  );
}
